import { last } from 'lodash';
import { useUpdate } from '../../../../contexts/UpdateContext/UpdateContext';
import { InterventionContainerProps } from '../../type';
import { useLoggedInUserFromContext } from '../../../../contexts/loggedInUserContext/LoggedInUserContext';
import { CgmGoalCardComponent } from '../../component/CgmGoalCardComponent/CgmGoalCardComponent';
import { PatientCommonCardComponent } from '../../../patient/component/PatientCardComponent/PatientCommonCardComponent';
import {
  CgmMetricEnum,
  CgmPercentGoal,
  CgmThresholdRangeClass,
  usePatientGetCgmGoalById,
  usePatientUpdateCgmGoal,
} from '../../../../uc-api-sdk';
import { CgmGoalFormContainer } from './CgmGoalFormContainer';
import { NestedFormComponentChildProps } from '../../../../contexts/NestedFormControlContext/type';
import { CgmGoalFormValue } from '../../component/CgmGoalFormComponent/CgmGoalFormComponent';
import { FetchComponent } from '../../../../uiComponent/FetchComponent/FetchComponent';
import { ApiRequestHelper } from '../../../../helpers/ApiRequest';
import { MixpanelEvents } from '../../../../contexts/MixpanelContext/MixpanelEvents';
import { useMixpanelContext } from '../../../../contexts/MixpanelContext/MixpanelContext';

export interface CgmGoalContainerProps
  extends Omit<InterventionContainerProps, 'onSubmit' | 'onError'>,
  NestedFormComponentChildProps<CgmGoalFormValue> {
  }

export const CgmGoalContainer = ({
  showEdit,
  onEdit,
  isEditing,
  onSubmit,
  onError,
  onCancel,
  patientId,
  onValuesChange,
  formButtons,
}: CgmGoalContainerProps) => {
  const { send } = useMixpanelContext();
  const { userInfo } = useLoggedInUserFromContext();
  const updateCgmGoalHook = usePatientUpdateCgmGoal({});
  const refetchCgmGoal = useUpdate('CGM_GOAL_UPDATED');

  const fetchCgmGoalHook = usePatientGetCgmGoalById({
    params: {
      memberId: patientId,
    },
  });

  const data = fetchCgmGoalHook.data?.data;

  const handleCancel = () => {
    onCancel?.();
  };

  const handleOnEdit = () => {
    onEdit?.();
  };

  const getThresholdValueHelper = (metric: CgmMetricEnum, isLowerBound?: boolean) => {
    const percentGoal = (data?.percentGoals)?.find(
      (i: CgmPercentGoal | {
        metric: CgmMetricEnum; thresholdRange: CgmThresholdRangeClass; goal: string
      }) => {
        if ('metric' in i && i.metric !== undefined) {
          return i.metric === metric;
        }
        return false;
      }
    );
    if (!percentGoal) return undefined;

    if (isLowerBound === undefined) {
      return Number(percentGoal.goal);
    }
    if (isLowerBound) {
      return Number(percentGoal?.thresholdRange?.lowerBound);
    }
    return Number(percentGoal?.thresholdRange?.upperBound);
  };

  const renderInitFormValue = () => {
    const initFormValue: CgmGoalFormValue = {
      tbrLevel2: getThresholdValueHelper(CgmMetricEnum.TBR_LEVEL_2) || 1,
      tbrLevel2UpperBound: getThresholdValueHelper(CgmMetricEnum.TBR_LEVEL_2, false) || 53,
      tbrLevel1: getThresholdValueHelper(CgmMetricEnum.TBR_LEVEL_1) || 4,
      tbrLevel1LowerBound: getThresholdValueHelper(CgmMetricEnum.TBR_LEVEL_1, true) || 54,
      tbrLevel1UpperBound: getThresholdValueHelper(CgmMetricEnum.TBR_LEVEL_1, false) || 69,
      tir: getThresholdValueHelper(CgmMetricEnum.TIR) || 70,
      tirLowerBound: getThresholdValueHelper(CgmMetricEnum.TIR, true) || 70,
      tirUpperBound: getThresholdValueHelper(CgmMetricEnum.TIR, false) || 179,
      tarLevel1: getThresholdValueHelper(CgmMetricEnum.TAR_LEVEL_1) || 25,
      tarLevel1LowerBound: getThresholdValueHelper(CgmMetricEnum.TAR_LEVEL_1, true) || 180,
      tarLevel1UpperBound: getThresholdValueHelper(CgmMetricEnum.TAR_LEVEL_1, false) || 249,
      tarLevel2: getThresholdValueHelper(CgmMetricEnum.TAR_LEVEL_2) || 5,
      tarLevel2LowerBound: getThresholdValueHelper(CgmMetricEnum.TAR_LEVEL_2, true) || 250,
      status: data?.goalStatus || undefined,
      latestNote: last(data?.notes || [])?.note || undefined,
      notes: data?.notes || [],
      updatedTime: data?.updatedAt || undefined
    };
    return initFormValue;
  };

  const buildPercentGoals = (metricName: CgmMetricEnum, goal: number) => {
    if (data && data.percentGoals) {
      const target = data.percentGoals.find(i => i.metric === metricName);
      if (target) {
        target.goal = goal;
      }
    }
    return data;
  };

  const buildCgmGoalRequest = (formValue: CgmGoalFormValue) => {
    const goals = [
      { metric: CgmMetricEnum.TBR_LEVEL_2, value: formValue?.tbrLevel2 || 1 },
      { metric: CgmMetricEnum.TBR_LEVEL_1, value: formValue?.tbrLevel1 || 4 },
      { metric: CgmMetricEnum.TIR, value: formValue?.tir || 70 },
      { metric: CgmMetricEnum.TAR_LEVEL_1, value: formValue?.tarLevel1 || 25 },
      { metric: CgmMetricEnum.TAR_LEVEL_2, value: formValue?.tarLevel2 || 5 },
    ];

    goals.forEach(goal => buildPercentGoals(goal.metric, goal.value));
    if (data) {
      data.notes = [{ note: formValue.latestNote ?? '' }];
      data.goalStatus = formValue.status;
      data.updatedBy = userInfo?.id;
    }
    return data;
  };

  const handleSubmit = (formValue: CgmGoalFormValue) => {
    const cgmGoalReq = buildCgmGoalRequest(formValue);

    if (cgmGoalReq) {
      ApiRequestHelper.tryCatch(
        updateCgmGoalHook.send({
          params: {
            request: cgmGoalReq,
            memberId: patientId
          },
        }),
        {
          success: 'Updated CGM goals!',
          error: 'Failed to update CGM goals!',
          onSuccess: () => {
            onSubmit?.();
            refetchCgmGoal.updateValue();
            fetchCgmGoalHook.refetch();
            send({ event: MixpanelEvents.InterventionCgmGoalUpdate, patientId });
          },
          onError,
        }
      );
    }
  };

  return (
    <PatientCommonCardComponent
      title="CGM Goal"
      showHeaderOutside={false}
      isEditing={isEditing}
      showEdit={showEdit}
      onEdit={handleOnEdit}
      updateInfo={fetchCgmGoalHook.data?.data}
      content={(
        <FetchComponent info={fetchCgmGoalHook}>
          <CgmGoalCardComponent
            cgmGoal={renderInitFormValue()}
          />
        </FetchComponent>
      )}
      formContent={(
        <CgmGoalFormContainer
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={fetchCgmGoalHook.isLoading}
          initialValues={renderInitFormValue()}
          onValuesChange={onValuesChange}
          formButtons={formButtons}
        />
      )}
    />
  );
};
